import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, ChevronDown, Check, Sparkles } from 'lucide-react';
import { fascialWorkItems } from '../data/fascialWork';

const renderText = (t) => t.split('\n').map((line, i) => (
  <span key={i}>{line}{i < t.split('\n').length - 1 && <br />}</span>
));

const fascialLines = [
  { img: '/deep-front-line.png', name: 'Deep Front Line', desc: "The body's core fascial pathway. It runs from the inner arch of the foot, up the inseam of the leg, through the pelvis, along the front of the spine, and up to the base of the skull. Intimately linked to breathing and walking, it connects the diaphragm, psoas, and deep stabilizers." },
  { img: '/superficial-front-line.png', name: 'Superficial Front Line', desc: 'Runs from the tops of the toes, up through the shin, quadriceps, and abdominals, to the skull. It balances the Superficial Back Line and plays a key role in posture — flexing the trunk and hips and extending the knee.' },
  { img: '/superficial-back-line.png', name: 'Superficial Back Line', desc: 'Runs from the bottoms of the toes, around the heel, up the entire back of the body — calves, hamstrings, erector spinae — and over the head to the brow ridge. It supports the body in standing and spans all of the spinal curves.' },
  { img: '/lateral-line.png', name: 'Lateral Line', desc: 'Runs along each side of the body from the foot, up the outer leg and thigh, along the trunk in a woven pattern, to the skull behind the ear. It acts as a brake for lateral and rotational movements and balances left and right like tent guy-wires.' },
  { img: '/spiral-line.png', name: 'Spiral Line', desc: 'Winds around the body in a double helix — from the skull, across the back to the opposite shoulder, around the front of the torso, down across the hip, knee, and foot arch, then back again. It mediates rotations and helps maintain balance in all planes of movement.' },
  { img: '/functional-lines.png', name: 'Functional Lines', desc: 'These lines extend the arm lines across the body surface, connecting them through the trunk to the opposite leg. The front functional line runs from the shoulder across the chest and abdomen to the opposite hip. The back functional line crosses from the shoulder, down the back, through the glutes to the opposite leg. They are essential for powerful, coordinated movements like throwing, reaching, and walking.' },
  { img: '/arm-lines.png', name: 'Arm Lines', desc: 'Four distinct fascial lines run from the chest, shoulder, and spine through the arms to the fingers. They include the deep and superficial front arm lines, and the deep and superficial back arm lines. These lines connect the hands and arms to the ribcage and spine, influencing grip strength, shoulder stability, and the integration of upper body movement.' },
];

const LearnFasciaPage = () => {
  const [openItem, setOpenItem] = useState(null);
  return (
    <div className="bg-cream min-h-screen pt-24" data-testid="learn-fascia-page">
      {/* Hero */}
      <section className="max-w-4xl mx-auto px-6 text-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <p className="text-xs font-body text-terracotta uppercase tracking-[0.25em] mb-3">The Power & Logic of Fascia</p>
          <h1 className="font-heading text-4xl sm:text-5xl text-forest mb-5">Discover Fascia</h1>
          <p className="text-stone-600 font-body text-lg leading-relaxed max-w-2xl mx-auto">
            Fascia is the continuous web of connective tissue that wraps and links every muscle, bone, nerve, and organ
            in your body — one single, uninterrupted fabric from head to toe. It gives us shape, transmits force, and
            carries information faster than we ever imagined. When it's supple and hydrated, we move with ease. When it
            gets compressed, dry, or stuck, it can pull the whole system out of balance.
          </p>
        </motion.div>
      </section>

      {/* Why it matters */}
      <section className="max-w-4xl mx-auto px-6 mt-12">
        <div className="bg-forest/5 rounded-2xl p-6 md:p-8">
          <p className="text-stone-600 font-body leading-relaxed">
            Because fascia is <span className="font-semibold text-forest">one connected system</span>, tension in one
            area can create pull and change somewhere far away. This is the logic behind fascial work: by releasing and
            rehydrating the tissue along its natural pathways, we can create a ripple of ease through the entire body.
            Below are the major <span className="font-semibold text-forest">fascial lines</span> — the continuous
            pathways of connective tissue mapped in Thomas Myers' <em>Anatomy Trains</em>.
          </p>
        </div>
      </section>

      {/* Video: Day 8 Stress Reset */}
      <section className="max-w-4xl mx-auto px-6 mt-12" data-testid="fascia-video-section">
        <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
          <div className="px-6 pt-5 pb-3">
            <h2 className="font-heading text-2xl text-forest">Feel It — The Stress Reset</h2>
            <p className="text-stone-500 font-body text-sm mt-1">A gentle first practice to experience your fascia in motion.</p>
          </div>
          <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
            <iframe
              className="absolute inset-0 w-full h-full"
              src="https://www.youtube.com/embed/2H5G_xddmTQ"
              title="Stress Reset — Fascia Practice"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      </section>

      {/* Fascial Lines */}
      <section className="max-w-4xl mx-auto px-6 mt-12" data-testid="fascial-lines-section">
        <div className="bg-white rounded-2xl border-2 border-terracotta/30 overflow-hidden">
          <div className="bg-terracotta px-6 py-4">
            <h2 className="font-heading text-2xl text-white">The Fascial Lines</h2>
            <p className="text-cream/80 font-body text-sm mt-1">Based on Thomas Myers' Anatomy Trains</p>
          </div>
          <div className="p-6">
            <p className="text-stone-600 font-body leading-relaxed mb-6">
              These fascial lines represent continuous pathways of connective tissue that run through the body.
              Understanding them helps illustrate why releasing one area can create change throughout the entire system.
            </p>
            <div className="space-y-6">
              {fascialLines.map((line) => (
                <div key={line.name} className="flex flex-col sm:flex-row gap-4 items-start bg-forest/5 rounded-xl p-4" data-testid={`fascia-line-${line.name.replace(/\s+/g, '-').toLowerCase()}`}>
                  <div className="flex-shrink-0 mx-auto sm:mx-0">
                    <img src={line.img} alt={line.name} className="h-48 w-auto object-contain rounded-lg" loading="lazy" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-heading text-lg text-forest mb-2">{line.name}</h3>
                    <p className="text-stone-600 font-body text-sm leading-relaxed">{line.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Video: Human Garage keynote — go deeper */}
      <section className="max-w-4xl mx-auto px-6 mt-12" data-testid="fascia-keynote-section">
        <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
          <div className="px-6 pt-5 pb-3">
            <h2 className="font-heading text-2xl text-forest">Go Deeper — Fascia &amp; How to Program the Body</h2>
            <p className="text-stone-500 font-body text-sm mt-1">A keynote from Jason, a founder of Human Garage. Want to dive a little deeper into the power of fascia? Watch and experience this talk.</p>
          </div>
          <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
            <iframe
              className="absolute inset-0 w-full h-full"
              src="https://www.youtube.com/embed/f253Dr7Lys4"
              title="Fascia & How to Program the Body — Human Garage Keynote"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      </section>

      {/* Ways to Experience This Work — collapsible offerings */}
      <section className="max-w-4xl mx-auto px-6 mt-12" data-testid="fascia-work-section">
        <div className="bg-white rounded-2xl border border-stone-200 p-6 md:p-8">
          <h2 className="font-heading text-2xl text-forest mb-1">Ways to Experience This Work</h2>
          <p className="text-stone-500 font-body text-sm mb-6">Two hands-on and movement-based offerings with Jill.</p>
          <div className="space-y-3">
            {fascialWorkItems.map((item) => {
              const isOpen = openItem === item.title;
              const c = item.content;
              return (
                <div key={item.title} className="border border-stone-200 rounded-xl overflow-hidden" data-testid={`fascia-work-item-${item.title.replace(/\s+/g, '-').toLowerCase()}`}>
                  <button
                    onClick={() => setOpenItem(isOpen ? null : item.title)}
                    className="w-full flex items-center gap-3 px-4 py-4 text-left hover:bg-stone-50 transition-colors"
                    data-testid={`fascia-work-toggle-${item.title.replace(/\s+/g, '-').toLowerCase()}`}
                  >
                    <Check size={18} className="text-terracotta flex-shrink-0" />
                    <span className="flex-1 font-body text-forest text-base">{item.title}</span>
                    <ChevronDown size={18} className={`text-stone-400 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25 }} className="overflow-hidden">
                        <div className="px-5 pb-6 pt-1 bg-forest/[0.03]">
                          <h3 className="font-heading text-xl text-forest">{c.heading}</h3>
                          <p className="font-body text-terracotta text-sm mb-4">{c.subheading}</p>
                          {c.intro && <p className="font-body text-stone-600 text-sm leading-relaxed mb-5">{renderText(c.intro)}</p>}
                          <div className="space-y-5">
                            {c.sections.map((s, si) => (
                              <div key={si}>
                                <h4 className="font-heading text-base text-forest mb-1">{s.title}</h4>
                                {s.text && <p className="font-body text-stone-600 text-sm leading-relaxed">{renderText(s.text)}</p>}
                                {s.inlineList && (
                                  <p className="font-body text-forest text-sm font-medium my-1">{s.inlineList.join(' · ')}</p>
                                )}
                                {s.afterList && <p className="font-body text-stone-600 text-sm leading-relaxed">{renderText(s.afterList)}</p>}
                                {s.bullets && (
                                  <ul className="mt-2 space-y-1">
                                    {s.bullets.map((b, bi) => (
                                      <li key={bi} className="flex items-start gap-2 font-body text-stone-600 text-sm">
                                        <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-terracotta flex-shrink-0" />{b}
                                      </li>
                                    ))}
                                  </ul>
                                )}
                                {s.afterBullets && <p className="font-body text-stone-600 text-sm leading-relaxed mt-2">{renderText(s.afterBullets)}</p>}
                                {s.benefitsIntro && <p className="font-body text-stone-600 text-sm leading-relaxed mt-3 mb-1">{s.benefitsIntro}</p>}
                                {s.benefits && (
                                  <ul className="space-y-1">
                                    {s.benefits.map((b, bi) => (
                                      <li key={bi} className="flex items-start gap-2 font-body text-stone-600 text-sm">
                                        <Check size={14} className="text-sage flex-shrink-0 mt-0.5" />{b}
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                            ))}
                          </div>
                          {c.bookingLink && (
                            <a
                              href={c.bookingLink.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-2 mt-5 bg-forest text-cream font-body text-sm px-5 py-2.5 rounded-full hover:bg-forest-dark transition-colors"
                              data-testid={`fascia-work-booking-${item.title.replace(/\s+/g, '-').toLowerCase()}`}
                            >
                              {c.bookingLink.label} <ChevronRight size={15} />
                            </a>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-4xl mx-auto px-6 my-16">
        <div className="bg-gradient-to-br from-forest to-forest-dark rounded-2xl p-8 md:p-10 text-center">
          <Sparkles size={26} className="text-terracotta mx-auto mb-4" />
          <h2 className="font-heading text-2xl text-cream mb-3">Ready to feel it for yourself?</h2>
          <p className="text-cream/85 font-body leading-relaxed max-w-xl mx-auto mb-6">
            Experience fascial work through gentle, guided practices in the 28-Day Stress Reset — waking up your tissue,
            freeing your movement, and restoring flow through the whole body.
          </p>
          <Link
            to="/programs/fascia-reset"
            className="inline-flex items-center gap-2 bg-cream text-forest font-body font-medium px-8 py-3 rounded-full hover:bg-white transition-colors"
            data-testid="learn-fascia-cta"
          >
            Explore the Stress Reset <ChevronRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default LearnFasciaPage;
